import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/PersonAddAlt1';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EmailIcon from '@mui/icons-material/Email';
import CallIcon from '@mui/icons-material/Call';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import apiService, { authFetch } from '../../services/apiServices';
import configService from '../../services/configService';
import dateFormats from '../../services/dateFormats';
import { exportEmployeesExcel } from '../../utils/employeesExport';
import { Link } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/RestoreFromTrash';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import globals from '../../services/globals';
import StaffImportDialog from './StaffImportDialog';
import { jsPDF } from 'jspdf';
import { decisionPdfBase64, fillDecisionPlaceholders } from '../../utils/decisionPdf';
import Toast from '../actions/Toasts';
import {
  formatCustomFieldValue,
  rawCustomFieldValue,
  getSearchableFields,
  getExportableFields,
} from '../../utils/customFieldHelpers';

const TERMINATE_REASON_TEMPLATES_DEFAULT = [
  'Skadim kontrate',
  'Largim vullnetar (dorëheqje)',
  'Marrëveshje reciproke',
  'Zvogëlim i stafit / Ristrukturim',
  'Shkelje disiplinore',
  'Periudha provuese nuk u kalua',
  'Mosparaqitje e pajustifikuar në punë',
  'Lëvizje e brendshme / Transferim',
  'Përfundim i projektit',
].map((label, i) => ({ id: i, label, comment: '' }));

function getTerminateReasonTemplates(t) {
  return [
    t('worker.terminateReasons.contractExpiry'),
    t('worker.terminateReasons.voluntaryResignation'),
    t('worker.terminateReasons.mutualAgreement'),
    t('worker.terminateReasons.restructuring'),
    t('worker.terminateReasons.misconduct'),
    t('worker.terminateReasons.probationFailed'),
    t('worker.terminateReasons.unauthorizedAbsence'),
    t('worker.terminateReasons.internalTransfer'),
    t('worker.terminateReasons.projectCompletion'),
  ].map((label, i) => ({ id: i, label, comment: '' }));
}

// PDF-ja e ndërprerjes ndjek strukturën e "Shabllonet → Vendimet" (utils/decisionPdf).
const TERMINATION_TYPE_FALLBACK = {
  code: 'termination', name: 'Ndërprerje e kontratës',
  title: 'VENDIM PËR NDËRPRERJEN E KONTRATËS SË PUNËS',
  legal_basis: 'Në mbështetje të Ligjit të Punës Nr. 03/L-212, punëdhënësi merr këtë',
};

export default function Directory() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [all, setAll] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState('all');
  const [contractsByWorker, setContractsByWorker] = useState(new Map());
  const [workTypes, setWorkTypes] = useState([]);
  const [regOpen, setRegOpen] = useState(false);
  const [regWorker, setRegWorker] = useState(null);
  const [regDate, setRegDate] = useState('');
  const [regComment, setRegComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailWorker, setEmailWorker] = useState(null);
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailHtmlBody, setEmailHtmlBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', variant: 'success' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Terminate / Reactivate ────────────────────────────────────────────────
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [terminateComment, setTerminateComment] = useState('');
  const [terminateDate, setTerminateDate] = useState('');
  const [terminateLeave, setTerminateLeave] = useState(null); // { typeName, totalDays, usedDays, availableDays }
  const [terminateLoading, setTerminateLoading] = useState(false);
  const [terminateSaving, setTerminateSaving] = useState(false);
  const [terminateTemplates, setTerminateTemplates] = useState(() => getTerminateReasonTemplates(t));
  // Struktura e PDF-së, lloji "ndërprerje" dhe profili i kompanisë — merren kur hapet dritarja.
  const [decisionCtx, setDecisionCtx] = useState({ layout: {}, type: TERMINATION_TYPE_FALLBACK, company: {} });
  const [terminateSelectedId, setTerminateSelectedId] = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [reactivateSaving, setReactivateSaving] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive' | 'atk' | 'startingSoon'

  // ── Custom fields ─────────────────────────────────────────────────────────
  const [customFieldDefs, setCustomFieldDefs] = useState([]); // show_in_list fields
  const [customFieldValuesMap, setCustomFieldValuesMap] = useState({}); // { workerId: { fieldKey: value } }

  // ── Consent map: workerId → consent record ────────────────────────────────
  const [consentMap, setConsentMap] = useState({});
  const [consentDialogData, setConsentDialogData] = useState(null); // opened consent detail

  // ── Photo picker ──────────────────────────────────────────────────────────
  const [photoWorker, setPhotoWorker] = useState(null);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoPickerImage, setPhotoPickerImage] = useState('');
  const [photoSaving, setPhotoSaving] = useState(false);
  const [cardMenu, setCardMenu] = useState(null); // { anchorEl, worker }

  const currentUser = globals().getLogedInUser();
  const currentRoles = (currentUser?.data?.roles || currentUser?.roles || [])
    .map(r => String(r?.name || r?.role_name || r || '').toLowerCase().trim());
  // Keep these in sync with TERMINATION_VIEW_ROLES in backend/server.js — both
  // sides must agree on who can view/initiate termination consents.
  const canTerminate  = currentRoles.some(r =>
    ['admin','superadmin','super_admin','company_admin','hr','hr_manager','menaxher','manager'].includes(r),
  );
  const canReactivate = currentRoles.some(r =>
    ['admin','superadmin','super_admin','company_admin','menaxher','manager'].includes(r),
  );
  // Njëjtë me requireRole-in e `/api/workers/import`: butoni nuk guxon të premtojë
  // një veprim që serveri do ta kthente me 403.
  const canImport = currentRoles.some(r =>
    ['admin','superadmin','super_admin','company_admin','hr','hr_manager','menaxher','manager'].includes(r),
  );

  const openCardMenu  = (e, w) => { e.preventDefault(); e.stopPropagation(); setCardMenu({ anchorEl: e.currentTarget, worker: w }); };
  const closeCardMenu = () => setCardMenu(null);
  const cmWorker = cardMenu?.worker;

  const todayYMDStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  // Load custom field definitions and all values for the workers module
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await configService.getModuleByKey('workers');
        if (!cancelled && mod?.success) {
          const allFields = mod.data?.fields || [];
          setCustomFieldDefs(allFields.filter(f => f.is_active && f.show_in_list));
        }
      } catch (_) {}
      try {
        const vals = await configService.getAllCustomFieldValues('workers');
        if (!cancelled && vals?.success !== false) {
          setCustomFieldValuesMap(vals?.data || vals || {});
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchConsents = async () => {
    // Vetëm rolet HR/admin/menaxher kanë leje për këtë endpoint (backend kthen 403 ndryshe)
    if (!canTerminate) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const res = await authFetch(`${apiUrl}/api/termination-consents`).catch(() => null);
      const r = res ? await res.json().catch(() => null) : null;
      if (r?.success && Array.isArray(r.data)) {
        const map = {};
        r.data.forEach(c => { map[String(c.workerId)] = c; });
        setConsentMap(map);
      }
    } catch (_) {}
  };

  const openTerminate = async (w) => {
    setTerminateTarget(w);
    setTerminateComment('');
    setTerminateDate(todayYMDStr());
    setTerminateLeave(null);
    setTerminateSelectedId(null);
    setTerminateLoading(true);
    // Load dynamic templates
    try {
      const [tplRes, typesRes, layoutRes, settingsRes] = await Promise.all([
        authFetch('/api/termination-templates?type=termination').then(x => x.json()).catch(() => null),
        authFetch('/api/decision-types').then(x => x.json()).catch(() => null),
        authFetch('/api/decision-settings').then(x => x.json()).catch(() => null),
        apiService().get('settings').catch(() => null),
      ]);
      if (tplRes?.success && Array.isArray(tplRes.data)) {
        const active = tplRes.data.filter(t => t.active !== false);
        if (active.length) setTerminateTemplates(active);
      }
      setDecisionCtx({
        layout: layoutRes?.success ? (layoutRes.data || {}) : {},
        type: (typesRes?.data || []).find(dt => dt.code === 'termination') || TERMINATION_TYPE_FALLBACK,
        company: settingsRes?.data?.[0] || {},
      });
    } catch (_) {}
    try {
      const yr = new Date().getFullYear();
      const r = await authFetch('/api/query/getLeaveBalances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workerId: w.Id, year: yr }),
      }).then(x => x.json()).catch(() => ({ success: false }));
      if (r?.success && r.data?.length) {
        const annual = r.data.find(b => /vjetor|annual|vjetar/i.test(b.LeaveTypeName||'')) || r.data[0];
        setTerminateLeave({
          typeName:      annual?.LeaveTypeName || '',
          totalDays:     (+(annual?.AccruedDays||0)) + (+(annual?.CarriedOver||0)),
          usedDays:      +(annual?.UsedDays||0),
          pendingDays:   +(annual?.PendingDays||0),
          availableDays: +(annual?.AvailableDays||0),
        });
      }
    } catch (_) {}
    setTerminateLoading(false);
  };

  const confirmTerminate = async () => {
    if (!terminateTarget) return;
    setTerminateSaving(true);
    try {
      const userEmail = currentUser?.data?.email || '';
      const userRole = currentRoles[0] || '';
      let pdfBase64 = null;
      try {
        pdfBase64 = decisionPdfBase64({
          ...decisionCtx,
          worker: terminateTarget,
          date: terminateDate,
          body: terminateComment,
        });
      } catch (pdfErr) { console.error('[PDF generation failed]', pdfErr); }
      const r = await authFetch(`/api/workers/${terminateTarget.Id}/terminate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': userEmail,
          'x-user-role': userRole,
        },
        body: JSON.stringify({ comment: terminateComment, terminationDate: terminateDate, pdfBase64 }),
      }).then(x => x.json()).catch(e => ({ success: false, error: e.message }));
      if (r?.success) {
        setAll(prev => prev.map(x => String(x.Id)===String(terminateTarget.Id) ? { ...x, Active: false } : x));
        setTerminateTarget(null);
        fetchConsents();
        setToast({
          open: true,
          message: r.data?.emailSent
            ? `${t('worker.messages.contractTerminatedSuccess')} ${t('worker.messages.contractTerminatedEmailAccepted')}`
            : `${t('worker.messages.contractTerminatedSuccess')} ${t('worker.messages.contractTerminatedEmailFailed', { reason: r.data?.emailError || 'arsye e panjohur' })}`,
          variant: r.data?.emailSent ? 'success' : 'warning',
        });
      } else {
        setToast({ open: true, message: r?.error || t('worker.messages.terminationError'), variant: 'error' });
      }
    } finally {
      setTerminateSaving(false);
    }
  };

  const openReactivate = (w) => setReactivateTarget(w);
  const confirmReactivate = async () => {
    if (!reactivateTarget) return;
    setReactivateSaving(true);
    try {
      const userEmail = currentUser?.data?.email || '';
      const userRole = currentRoles[0] || '';
      const r = await authFetch(`/api/workers/${reactivateTarget.Id}/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': userEmail,
          'x-user-role': userRole,
        },
      }).then(x => x.json()).catch(e => ({ success: false, error: e.message }));
      if (r?.success) {
        setAll(prev => prev.map(x => String(x.Id)===String(reactivateTarget.Id) ? { ...x, Active: true } : x));
        setReactivateTarget(null);
        setToast({ open: true, message: t('worker.messages.reactivatedSuccess'), variant: 'success' });
      } else {
        setToast({ open: true, message: r?.error || t('worker.messages.reactivationError'), variant: 'error' });
      }
    } finally {
      setReactivateSaving(false);
    }
  };

  // Normalize API rows to the shape the UI expects
  const normalizeWorkerRow = (row = {}) => ({
    ...row,
    Id:         row?.Id         ?? row?.id,
    PersonalId: row?.PersonalId ?? row?.PersonalNumber ?? row?.personal_id ?? row?.personalid,
    FirstName:  row?.FirstName  ?? row?.first_name  ?? row?.firstname,
    ParentName: row?.ParentName ?? row?.parent_name ?? row?.parentname,
    LastName:   row?.LastName   ?? row?.last_name   ?? row?.lastname,
    Email:      row?.Email      ?? row?.email,
    Phone:      row?.Phone      ?? row?.phone,
    Qualification: row?.Qualification ?? row?.qualification,
    Address:    row?.Address    ?? row?.address,
    Place:      row?.Place      ?? row?.place ?? row?.Address ?? row?.address ?? row?.city,
    WorkType:   row?.WorkType   ?? row?.work_type ?? row?.employment_type,
    Department: row?.Department ?? row?.department,
    Branch:     row?.Branch     ?? row?.branch,
    Position:   row?.Position   ?? row?.position,
    StartDate:  row?.StartDate  ?? row?.HireDate ?? row?.start_date,
    EndDate:    row?.EndDate    ?? row?.end_date,
    Image:      row?.Image      ?? row?.image,
    ATKRegistered:   row?.ATKRegistered   ?? row?.atkRegistered,
    ATKRegisteredAt: row?.ATKRegisteredAt ?? row?.atkRegisteredAt,
  });

  const normalizeContractRow = (row = {}) => ({
    ...row,
    Id: row?.Id ?? row?.id,
    WorkerId:
      row?.WorkerId ??
      row?.worker_id ??
      row?.workerid ??
      row?.Employee ??
      row?.employee ??
      row?.employee_id,
    StartDate: row?.StartDate ?? row?.start_date,
    EndDate: row?.EndDate ?? row?.end_date,
    WorkType: row?.WorkType ?? row?.work_type ?? row?.employment_type,
    EmploymentType: row?.EmploymentType ?? row?.employment_type,
    EmployerName: row?.EmployerName ?? row?.employer_name ?? row?.punedhenesi ?? row?.employer,
    Position: row?.Position ?? row?.position,
    Qualification: row?.Qualification ?? row?.qualification,
  });


  const getWorkTypeName = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const n = Number(val);
    if (!isNaN(n)) {
      const wt = (workTypes || []).find((x) => String(x.Id || x.id) === String(n));
      return wt?.Name || wt?.name || String(val);
    }
    return String(val);
  };

  const getPrimaryContract = (worker) => {
    if (!worker) return null;
    const key = worker?.Id ?? worker?.id ?? worker?.WorkerId ?? worker?.EmployeeId;
    if (key !== undefined && key !== null) {
      const stored = contractsByWorker.get(String(key));
      if (stored) return stored;
    }
    return worker?.Contract || null;
  };

  const getWorkerDates = (worker, contractOverride) => {
    const contract = contractOverride || getPrimaryContract(worker);
    const startSource =
      contract?.StartDate ||
      worker?.StartDate ||
      worker?.Contract?.StartDate ||
      worker?.ContractStartDate ||
      worker?.start_date;
    const endSource =
      contract?.EndDate ||
      worker?.EndDate ||
      worker?.Contract?.EndDate ||
      worker?.ContractEndDate ||
      worker?.end_date;
    return {
      contract,
      startDate: dateFormats(startSource) || '—',
      endDate: dateFormats(endSource) || '—',
    };
  };

  const getEmployerDisplay = (worker, contractOverride) => {
    const contract = contractOverride || getPrimaryContract(worker);
    return (
      contract?.EmployerName ||
      worker?.EmployerName ||
      worker?.CompanyName ||
      getWorkTypeName(contract?.WorkType ?? worker?.WorkType) ||
      '—'
    );
  };

  const getProfessionDisplay = (worker, contractOverride) => {
    const contract = contractOverride || getPrimaryContract(worker);
    const q = String(worker?.Qualification || '').toLowerCase();
    const autoProfession = q.includes('matemat')
      ? 'Matematikan'
      : q.includes('informatik')
        ? 'Programer / Inxhinier Softueri'
        : q.includes('ekonom')
          ? 'Ekonomist'
          : q.includes('jurid')
            ? 'Jurist'
            : '';
    return worker?.Profession || autoProfession || contract?.WorkType || worker?.WorkType || '—';
  };

  const buildWorkerRegistrationSnapshot = (worker) => {
    if (!worker) {
      return {
        info: [],
        fullName: '—',
        email: '—',
        personalId: '—',
      };
    }
    const { contract, startDate, endDate } = getWorkerDates(worker);
    const employerName = getEmployerDisplay(worker, contract);
    const profession = getProfessionDisplay(worker, contract);
    const fullName = `${worker?.FirstName || ''} ${worker?.LastName || ''}`.trim() || '—';
    const phone = worker?.Phone || worker?.phone || '—';
    const email = worker?.Email || worker?.email || '—';
    const residency = worker?.Place || worker?.Address || worker?.City || worker?.Municipality || '—';
    const personalId = worker?.PersonalId || worker?.IdNumber || '—';
    const info = [
      { label: 'Emri i plotë', value: fullName },
      { label: 'Numri i ID', value: personalId },
      { label: 'Punëdhënësi', value: employerName },
      { label: 'Data e fillimit', value: startDate },
      { label: 'Data e përfundimit', value: endDate },
      { label: 'Telefoni', value: phone },
      { label: 'Email', value: email },
      { label: 'Vendbanimi', value: residency },
      { label: 'Niveli i shkollimit', value: worker?.Qualification || '—' },
      { label: 'Profesioni/Pozita', value: profession },
    ];
    return {
      info,
      fullName,
      email,
      personalId,
    };
  };

  const buildEmailSubject = (worker) => {
    if (!worker) return 'Regjistrimi i punëtorit';
    const fullName = `${worker?.FirstName || ''} ${worker?.LastName || ''}`.trim();
    return fullName ? `Regjistrimi i ${fullName}` : 'Regjistrimi i punëtorit';
  };

  const buildEmailPlainBody = (worker) => {
    const snapshot = buildWorkerRegistrationSnapshot(worker);
    if (!snapshot.info.length) return '';
    const lines = [
      'Përshëndetje,',
      '',
      'Ju njoftojmë se po dorëzojmë kërkesën për regjistrim dhe regjistrim në ATK për punëtorin e mëposhtëm.',
      'Të gjitha informacionet zyrtare janë përfshirë në PDF-në profesionale të bashkangjitur.',
      'Ju lutem hapni dokumentin për të parë detajet e plota ose për t’i arkivuar ato si pjesë e dokumentacionit të brendshëm.',
      '',
      'Ky email u gjenerua automatikisht nga sistemi HRT3.',
      'Faleminderit.',
    ];
    return lines.join('\n');
  };

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const buildEmailHtmlBody = (worker) => {
    const snapshot = buildWorkerRegistrationSnapshot(worker);
    if (!snapshot.info.length) return '';
    return `<!DOCTYPE html>
<html lang="sq">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Regjistrimi i punëtorit</title>
  </head>
  <body style="margin:0;background:#f4f5ff;font-family:'Inter','Segoe UI',Arial,sans-serif;color:#10152b;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;background:#f4f5ff;padding:32px 16px;border-collapse:collapse;">
      <tr>
        <td align="center">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:620px;width:100%;background:#ffffff;border-radius:28px;overflow:hidden;border-collapse:separate;box-shadow:0 45px 70px -55px rgba(72,76,222,0.85);">
            <tr>
              <td style="background:linear-gradient(135deg,#5146ff,#6de2ff);padding:40px 42px;color:#ffffff;">
                <h1 style="margin:0 0 6px;font-size:26px;font-weight:700;font-family:'Inter','Segoe UI',Arial,sans-serif;">${escapeHtml(snapshot.fullName)}</h1>
                <p style="margin:0;font-size:14px;opacity:0.95;color:#ffffff;">${escapeHtml(snapshot.email)}</p>
                <span style="display:inline-block;margin-top:18px;padding:6px 18px;border-radius:999px;border:1px solid rgba(255,255,255,0.55);background:rgba(255,255,255,0.2);font-size:13px;letter-spacing:0.03em;">${escapeHtml(
                  snapshot.personalId && snapshot.personalId !== '—' ? `ID: ${snapshot.personalId}` : 'Punëtor i ri'
                )}</span>
              </td>
            </tr>
            <tr>
              <td style="padding:34px 42px 24px;">
                <p style="margin:0 0 16px;line-height:1.6;color:#10152b;">Përshëndetje,</p>
                <p style="margin:0 0 18px;line-height:1.6;color:#10152b;">
                  Ju njoftojmë se po dorëzojmë kërkesën për regjistrim dhe regjistrim në ATK për punëtorin e mëposhtëm.
                  Të gjitha informacionet zyrtare janë përfshirë në PDF-në profesionale të bashkangjitur në këtë email.
                </p>
                <p style="margin:0 0 24px;line-height:1.6;color:#10152b;">
                  Ju lutem hapni dokumentin për të parë detajet e plota ose për t’i arkivuar ato si pjesë e dokumentacionit të brendshëm.
                </p>
                <table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:separate;">
                  <tr>
                    <td style="background:#0f1a3a;color:#ffffff;padding:12px 24px;border-radius:999px;font-weight:600;font-size:13px;">
                      PDF e bashkangjitur: ${escapeHtml(buildWorkerRegistrationPdfFileName(snapshot))}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td style="padding:0 42px 40px;font-size:13px;color:#5d5f8b;line-height:1.6;">
                <p style="margin:0 0 8px;">Ky email u gjenerua automatikisht nga sistemi HRT3.</p>
                <p style="margin:0;">Faleminderit.</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
  };

  const sanitizeFileToken = (value) =>
    String(value || '')
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .toLowerCase() || 'punetor';

  const buildWorkerRegistrationPdfFileName = (snapshot) => {
    const token =
      snapshot?.fullName && snapshot.fullName !== '—'
        ? sanitizeFileToken(snapshot.fullName)
        : 'punetor';
    return `Regjistrimi-${token || 'punetor'}.pdf`;
  };

  const renderWorkerRegistrationPdf = (snapshot) => {
    if (!snapshot?.info?.length) return null;
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;

    // Header gradient band
    const headerHeight = 52;
    const gradientStops = [
      { r: 83, g: 75, b: 255 },
      { r: 92, g: 175, b: 255 },
    ];
    for (let i = 0; i < headerHeight; i++) {
      const t = i / headerHeight;
      const r = gradientStops[0].r * (1 - t) + gradientStops[1].r * t;
      const g = gradientStops[0].g * (1 - t) + gradientStops[1].g * t;
      const b = gradientStops[0].b * (1 - t) + gradientStops[1].b * t;
      doc.setDrawColor(r, g, b);
      doc.setFillColor(r, g, b);
      doc.rect(0, i, pageWidth, 1, 'F');
    }

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.text('Regjistrimi i punëtorit', margin, 18);
    doc.setFontSize(23);
    doc.text(snapshot.fullName === '—' ? 'Punëtor i ri' : snapshot.fullName, margin, 33);
    if (snapshot.email && snapshot.email !== '—') {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(snapshot.email, margin, 41);
    }

    // Badge
    doc.setDrawColor(255, 255, 255);
    doc.setFillColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.roundedRect(pageWidth - 80, 12, 64, 18, 4, 4, 'FD');
    doc.setTextColor(90, 102, 255);
    doc.text('HRT3 • PDF', pageWidth - 48, 23, { align: 'center' });

    // Body intro
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(20, 24, 46);
    let y = headerHeight + 12;
    doc.text('Të dhënat zyrtare të punëtorit (përmbledhur):', margin, y);
    y += 8;

    // Compact two-column layout to fit në një faqe
    const gutter = 6;
    const colWidth = (pageWidth - margin * 2 - gutter) / 2;
    const boxHeightBase = 14;

    snapshot.info.forEach(({ label, value }, idx) => {
      const col = idx % 2;
      const x = margin + col * (colWidth + gutter);
      // break to next row only when both cols filled
      if (idx % 2 === 0 && idx > 0) {
        // compute y shift only when starting new row after both columns placed
        y += boxHeightBase + 8;
        if (y > pageHeight - 30) {
          doc.addPage();
          y = margin;
        }
      }
      doc.setDrawColor(226, 231, 245);
      doc.setFillColor(249, 251, 255);
      doc.roundedRect(x, y, colWidth, boxHeightBase, 3, 3, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(110, 114, 150);
      doc.text(String(label).toUpperCase(), x + 4, y + 5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11.5);
      doc.setTextColor(18, 24, 58);
      const textLines = doc.splitTextToSize(String(value || '—'), colWidth - 8);
      doc.text(textLines, x + 4, y + 11);
      // adjust height if wrapped text is longer
      const extraHeight = Math.max(0, (textLines.length - 1) * 5);
      if (extraHeight > 0) {
        doc.setDrawColor(226, 231, 245);
        doc.setFillColor(249, 251, 255);
        doc.roundedRect(x, y, colWidth, boxHeightBase + extraHeight, 3, 3, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(110, 114, 150);
        doc.text(String(label).toUpperCase(), x + 4, y + 5);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11.5);
        doc.setTextColor(18, 24, 58);
        doc.text(textLines, x + 4, y + 11);
      }
    });

    // Footer note
    const footerY = Math.min(pageHeight - 24, y + boxHeightBase + 10);
    doc.setDrawColor(226, 231, 245);
    doc.line(margin, footerY, pageWidth - margin, footerY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(93, 95, 139);
    doc.text('Ky dokument u gjenerua automatikisht nga HRT3 dhe u dërgua si PDF.', margin, footerY + 9);
    doc.text('Faleminderit për bashkëpunimin.', margin, footerY + 16);

    return doc;
  };

  const buildWorkerRegistrationPdfAttachment = (worker) => {
    const snapshot = buildWorkerRegistrationSnapshot(worker);
    const doc = renderWorkerRegistrationPdf(snapshot);
    if (!doc) return null;
    const filename = buildWorkerRegistrationPdfFileName(snapshot);
    const dataUri = doc.output('datauristring');
    const base64 = dataUri.split(',')[1];
    return {
      filename,
      name: filename,
      originalName: filename,
      content: base64,
      encoding: 'base64',
      contentType: 'application/pdf',
      mimeType: 'application/pdf',
    };
  };

  const resolveSignedContract = (contract) => {
    if (!contract) return null;
    const data =
      contract.SignedContractFile ||
      contract.SignedContract ||
      contract.ContractFile ||
      contract.ContractDocument;
    if (!data) return null;
    return {
      data,
      name:
        contract.SignedContractName ||
        contract.ContractFileName ||
        contract.ContractDocumentName ||
        'kontrata',
      type:
        contract.SignedContractType ||
        contract.ContractFileType ||
        contract.ContractDocumentType ||
        '',
    };
  };

  const buildSignedContractAttachment = (contract) => {
    const file = resolveSignedContract(contract);
    if (!file) return null;
    let base64 = file.data;
    let contentType = file.type || '';
    // Strip data URL prefix if present
    if (typeof base64 === 'string' && base64.startsWith('data:')) {
      const match = base64.match(/^data:([^;]+);base64,(.*)$/);
      if (match) {
        contentType = contentType || match[1];
        base64 = match[2];
      }
    }
    return {
      filename: file.name || 'kontrata',
      name: file.name || 'kontrata',
      originalName: file.name || 'kontrata',
      content: base64,
      encoding: 'base64',
      contentType: contentType || 'application/octet-stream',
      mimeType: contentType || 'application/octet-stream',
    };
  };

  const openEmailDialog = (worker) => {
    setEmailWorker(worker);
    setEmailDialogOpen(true);
    setSecondaryEmail('');
    setEmailSubject(buildEmailSubject(worker));
    setEmailBody(buildEmailPlainBody(worker));
    setEmailHtmlBody(buildEmailHtmlBody(worker));
    setEmailError('');
    setEmailSuccess('');
  };

  const closeEmailDialog = () => {
    if (sendingEmail) return;
    setEmailDialogOpen(false);
    setEmailWorker(null);
    setEmailError('');
    setEmailSuccess('');
    setEmailHtmlBody('');
  };

  const confirmDelete = (worker) => {
    setDeleteTarget(worker);
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.Id) return;
    setDeleting(true);
    try {
      const resp = await apiService().delete('workers', deleteTarget.Id);
      if (resp?.success !== false) {
        setAll((prev) => prev.filter((w) => String(w.Id) !== String(deleteTarget.Id)));
        setToast({
          open: true,
          message: t('worker.messages.deletedSuccess'),
          variant: 'success',
        });
      } else {
        setToast({
          open: true,
          message: resp?.error || t('worker.messages.deleteError'),
          variant: 'error',
        });
      }
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleSendEmail = async () => {
    const recipients = [emailWorker?.Email, secondaryEmail.trim()].filter(Boolean);
    if (!recipients.length) {
      setEmailError(t('worker.messages.noEmailProvided'));
      return;
    }

    if (!emailSubject.trim()) {
      setEmailError(t('worker.messages.subjectRequired'));
      return;
    }

    try {
      setSendingEmail(true);
      setEmailError('');
      const payload = {
        secondaryEmail: secondaryEmail.trim(),
        subject: emailSubject.trim(),
        body: emailBody,
        html: emailHtmlBody,
      };
      const attachments = [];
      const pdfAttachment = buildWorkerRegistrationPdfAttachment(emailWorker);
      if (pdfAttachment) attachments.push(pdfAttachment);
      const contract = getPrimaryContract(emailWorker);
      const contractAttachment = buildSignedContractAttachment(contract);
      if (contractAttachment) attachments.push(contractAttachment);
      if (attachments.length) payload.attachments = attachments;
      const response = await authFetch(`/api/workers/${emailWorker.Id}/send-registration-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.error || 'Nuk u arrit të dërgohet emaili.');
      }
      setEmailSuccess('Serveri i emailit e pranoi mesazhin. Kontrollo edhe dosjen Spam nëse nuk mbërrin.');
      setTimeout(() => {
        setEmailDialogOpen(false);
        setEmailWorker(null);
        setEmailHtmlBody('');
      }, 1200);
    } catch (error) {
      const message =
        error?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Nuk u arrit të dërgohet emaili.';
      setEmailError(message);
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const [w, d, c, wt, br] = await Promise.all([
          apiService().get('workers'),
          apiService().get('department'),
          apiService().get('workerContract'),
          apiService().get('worktype'),
          // Degët i duhen vetëm importit (emër → id) dhe shabllonit që shkarkohet.
          // `branches`, JO `branch`: `workers.branch_id` referencon `branches(id)`,
          // ndaj një id nga tabela e vjetër do të binte si shkelje çelësi të huaj.
          // I njëjti burim që përdor EmployeeDetailReport për ta lexuar degën.
          apiService().get('branches').catch(() => ({ success: false })),
        ]);
        if (!mounted) return;
        if (w?.success) {
          // overlay local ATK registrations so changes persist for testing even if backend doesn't
          let list = (w.data || []).map(normalizeWorkerRow);
          try {
            const key = 'atkRegs';
            const map = JSON.parse(localStorage.getItem(key) || '{}');
            if (map && typeof map === 'object') {
              list = list.map((x) => {
                const add = map[String(x.Id || x.id)];
                return add ? { ...x, ...add } : x;
              });
            }
          } catch (_) {}
          setAll(list);
        }
        if (d?.success) setDepartments(d.data || []);
        if (br?.success) setBranches(br.data || []);
        if (wt?.success) setWorkTypes(wt.data || []);
        if (c?.success) {
          const list = (c.data || []).map(normalizeContractRow);
          // keep latest contract per WorkerId by StartDate
          const map = new Map();
          list.forEach((ct) => {
            const key = String(
              ct.WorkerId ||
              ct.Employee ||
              ct.employee ||
              ct.employee_id ||
              ct.Id
            );
            const prev = map.get(key);
            const currStart = new Date(ct.StartDate || 0).getTime();
            const prevStart = prev ? new Date(prev.StartDate || 0).getTime() : -Infinity;
            if (!prev || currStart >= prevStart) map.set(key, ct);
          });
          setContractsByWorker(map);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[WorkerList] Failed to load:', err);
        if (mounted) {
          setToast({ open: true, message: t('worker.messages.loadFailed'), variant: 'error' });
        }
      } finally {
        mounted && setLoading(false);
      }
    }
    load();
    fetchConsents();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!q) return all;
    const term = q.toLowerCase();
    const searchableCustomFields = getSearchableFields(customFieldDefs);
    return all.filter((w) => {
      const coreFields = [
        w?.FirstName,
        w?.ParentName,
        w?.LastName,
        w?.Email,
        w?.Phone,
        w?.Place,
      ]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase());
      if (coreFields.some((f) => f.includes(term))) return true;
      // Search custom field values
      if (searchableCustomFields.length > 0) {
        const cfValues = customFieldValuesMap[String(w?.Id || w?.id)] || {};
        return searchableCustomFields.some(f => {
          const v = cfValues[f.key];
          return v && String(v).toLowerCase().includes(term);
        });
      }
      return false;
    });
  }, [all, q, customFieldDefs, customFieldValuesMap]);

  const deptFiltered = useMemo(() => {
    if (selectedDept === 'all') return filtered;
    // Department on worker appears to be numeric id based on AddWorker; match to selected id
    return filtered.filter((w) => String(w?.Department) === String(selectedDept));
  }, [filtered, selectedDept]);

  // Workers whose contract start date falls within the next 2 days (inclusive of today)
  const isStartingSoon = (w) => {
    const contract = getPrimaryContract(w);
    const raw = contract?.StartDate || w?.StartDate || w?.Contract?.StartDate || w?.ContractStartDate || w?.start_date;
    if (!raw) return false;
    const start = new Date(raw);
    if (isNaN(start.getTime())) return false;
    const today = new Date();
    const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const s0 = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const diffDays = Math.round((s0 - t0) / 86400000);
    return diffDays >= 0 && diffDays <= 2;
  };

  const statusFiltered = useMemo(() => {
    if (statusFilter === 'all') return deptFiltered;
    return deptFiltered.filter((w) => {
      const inactive = w?.Active === false || w?.active === false;
      if (statusFilter === 'active')       return !inactive;
      if (statusFilter === 'inactive')     return inactive;
      if (statusFilter === 'atk')          return w?.ATKRegisteredAt && w?.ATKRegistered;
      if (statusFilter === 'startingSoon') return isStartingSoon(w);
      return true;
    });
  }, [deptFiltered, statusFilter, contractsByWorker]);

  const deptCounts = useMemo(() => {
    const counts = new Map();
    for (const w of all || []) {
      const key = String(w?.Department ?? 'none');
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [all]);

  const onExport = () => {
    // Excel, jo CSV: numri personal e humbte zeron e parë, diakritikat vareshin
    // nga lokalja e makinës, dhe skedari dilte pa kokë e pa datë.
    exportEmployeesExcel(filtered, {
      workTypes,
      exportFields: getExportableFields(customFieldDefs),
      cfValues: customFieldValuesMap,
      formatCustom: rawCustomFieldValue,
      formatDate: dateFormats,
      today: todayYMD(),
      labels: {
        title: t('worker.directory.title', 'LISTA E PUNËTORËVE').toUpperCase(),
        name: t('worker.fullName', 'Emri dhe mbiemri'),
        personalId: t('report.personalNr', 'Numri personal'),
        email: t('worker.email', 'Email'),
        phone: t('worker.phone', 'Telefoni'),
        position: t('report.position', 'Pozita'),
        total: t('worker.directory.totalEmployees', 'punonjës gjithsej'),
      },
    });
  };

  const todayYMD = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = ('0' + (d.getMonth() + 1)).slice(-2);
    const da = ('0' + d.getDate()).slice(-2);
    return `${y}-${m}-${da}`;
  };
  const openReg = (w) => { setRegWorker(w); setRegDate(todayYMD()); setRegComment(''); setRegOpen(true); };
  const closeReg = () => { if (!saving) setRegOpen(false); };
  const saveReg = async () => {
    if (!regWorker) return;
    setSaving(true);
    try {
      // Load full worker row to satisfy backend PUT requirements
      const existing = await apiService().query('getWorker', 'id=' + regWorker.Id);
      // Always fall back to the data we already have in the directory card so the PUT body is never empty
      const base =
        (existing?.success && existing.data && existing.data[0])
          ? existing.data[0]
          : { ...regWorker };
      const payload = { ...base, ATKRegisteredAt: regDate, ATKRegistered: true };
      const apiPayload = { ...payload };
      delete apiPayload.ATKComment; // ATKComment not yet a backend column
      await apiService().put('workers', apiPayload);
      // Also cache locally for instant UI updates across the session
      const localUpdate = { ATKRegisteredAt: regDate, ATKRegistered: true, ATKComment: regComment };
      try {
        const key = 'atkRegs';
        const map = JSON.parse(localStorage.getItem(key) || '{}');
        map[String(regWorker.Id)] = localUpdate;
        localStorage.setItem(key, JSON.stringify(map));
      } catch (_) {}
      setAll((prev) => (prev || []).map((x) => String(x.Id) === String(regWorker.Id)
        ? { ...x, ...localUpdate }
        : x));
      setRegOpen(false);
      setToast({
        open: true,
        message: t('worker.messages.atkRegisteredSuccess'),
        variant: 'success',
      });
    } catch (err) {
      console.error('[ATK] save failed', err);
      setToast({
        open: true,
        message: err?.message || t('worker.messages.terminationError'),
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Placeholder avatars ────────────────────────────────────────────────
  const PLACEHOLDER_AVATARS = React.useMemo(() => [
    { color: '#3B82F6', label: 'Kaltër' },
    { color: '#14B8A6', label: 'Teal' },
    { color: '#8B5CF6', label: 'Vjollcë' },
    { color: '#F97316', label: 'Portokall' },
    { color: '#EC4899', label: 'Rozë' },
    { color: '#22C55E', label: 'Gjelbër' },
    { color: '#EF4444', label: 'Kuq' },
    { color: '#A16207', label: 'Kafe' },
    { color: '#0891B2', label: 'Cyan' },
    { color: '#7C3AED', label: 'Indigo' },
    { color: '#DC2626', label: 'Skarlat' },
    { color: '#059669', label: 'Emerald' },
  ].map(({ color, label }, i) => {
    const isFemale = i % 3 === 2;
    const svg = isFemale
      ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50" fill="${color}"/><circle cx="50" cy="35" r="16" fill="rgba(255,255,255,0.93)"/><path d="M20 90 Q20 64 50 64 Q80 64 80 90" fill="rgba(255,255,255,0.93)"/><path d="M34 28 Q34 14 50 14 Q66 14 66 28" fill="${color}" stroke="rgba(255,255,255,0.7)" stroke-width="2"/><path d="M32 26 Q32 38 36 40" fill="${color}" stroke="rgba(255,255,255,0.5)" stroke-width="3" stroke-linecap="round"/><path d="M68 26 Q68 38 64 40" fill="${color}" stroke="rgba(255,255,255,0.5)" stroke-width="3" stroke-linecap="round"/></svg>`
      : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50" fill="${color}"/><circle cx="50" cy="36" r="16" fill="rgba(255,255,255,0.93)"/><path d="M20 90 Q20 64 50 64 Q80 64 80 90" fill="rgba(255,255,255,0.93)"/></svg>`;
    return { label, color, src: `data:image/svg+xml;base64,${btoa(svg)}` };
  }), []);

  const openPhotoDialog = (worker) => {
    setPhotoWorker(worker);
    setPhotoPickerImage(worker?.Image || '');
    setPhotoDialogOpen(true);
  };
  const closePhotoDialog = () => { if (!photoSaving) { setPhotoDialogOpen(false); setPhotoWorker(null); setPhotoPickerImage(''); } };

  const handlePhotoFile = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      const type = file.type || 'image/png';
      setPhotoPickerImage(`data:${type};base64,${base64}`);
    };
    reader.readAsDataURL(file);
  };

  const saveWorkerPhoto = async () => {
    if (!photoWorker) return;
    setPhotoSaving(true);
    try {
      const existing = await apiService().query('getWorker', 'id=' + photoWorker.Id);
      const base = (existing?.success && existing.data?.[0]) ? existing.data[0] : { ...photoWorker };
      const payload = { ...base, Image: photoPickerImage || null };
      delete payload.ATKRegistered;
      delete payload.ATKRegisteredAt;
      delete payload.Profession;
      const r = await apiService().put('workers', payload);
      if (r?.success !== false) {
        setAll(prev => prev.map(x => String(x.Id) === String(photoWorker.Id) ? { ...x, Image: photoPickerImage || null } : x));
        setToast({ open: true, message: t('worker.messages.photoSavedSuccess'), variant: 'success' });
        setPhotoDialogOpen(false);
        setPhotoWorker(null);
        setPhotoPickerImage('');
      } else {
        setToast({ open: true, message: r?.error || t('worker.messages.saveError'), variant: 'error' });
      }
    } catch (err) {
      console.error('[photo] save failed', err);
      setToast({ open: true, message: err?.message || t('worker.messages.saveError'), variant: 'error' });
    } finally {
      setPhotoSaving(false);
    }
  };

  // Per-department avatar palette — soft bg + dark fg, modern + calm
  const DEPT_PALETTE = [
    { bg: '#EEF2FF', fg: '#4F46E5' }, // indigo
    { bg: '#F0F9FF', fg: '#0369A1' }, // sky
    { bg: '#ECFDF5', fg: '#047857' }, // emerald
    { bg: '#FFFBEB', fg: '#B45309' }, // amber
    { bg: '#FDF2F8', fg: '#BE185D' }, // rose
    { bg: '#F5F3FF', fg: '#6D28D9' }, // violet
    { bg: '#F1F5F9', fg: '#475569' }, // slate
    { bg: '#FFF7ED', fg: '#C2410C' }, // orange
  ];

  // ── Header stat tabs (clickable filters) ───────────────────────────────────
  const headerStats = [
    { key: 'active',       label: t('common.active'),                  value: all.filter(w => w?.Active !== false && w?.active !== false).length, tone: 'success'   },
    { key: 'inactive',     label: t('common.inactive'),                value: all.filter(w => w?.Active === false || w?.active === false).length, tone: 'error'     },
    { key: 'atk',          label: 'ATK ✓',                              value: all.filter(w => w?.ATKRegisteredAt && w?.ATKRegistered).length,     tone: 'warning'   },
    { key: 'startingSoon', label: t('worker.directory.startingSoon'),  value: all.filter(isStartingSoon).length,                                  tone: 'info'      },
    { key: 'shown',        label: t('worker.directory.displayed'),     value: statusFiltered.length,                                              tone: 'primary'   },
  ];
  const toneBg = {
    success: alpha(theme.palette.success.main, 0.10),
    error:   alpha(theme.palette.error.main,   0.10),
    warning: alpha(theme.palette.warning.main, 0.10),
    info:    alpha(theme.palette.info.main,    0.10),
    primary: alpha(theme.palette.primary.main, 0.10),
  };

  return (
    <>
      <Toast {...toast} />
      <Box sx={{ px: { xs: 0, sm: 0 }, py: { xs: 1, sm: 1.5 } }}>
        <Stack spacing={{ xs: 2, md: 2.5 }}>

          {/* ── Page Header ─────────────────────────────────────────────────── */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={{ xs: 1.5, md: 2 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.375rem', md: '1.625rem' },
                  letterSpacing: '-0.025em',
                  color: 'text.primary',
                  lineHeight: 1.15,
                }}
              >
                {t('worker.directory.title')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                {loading ? '…' : `${all.length} ${t('worker.directory.totalEmployees')}`}
              </Typography>
            </Box>

            {/* Primary actions */}
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: '16px !important' }} />}
                onClick={onExport}
                sx={{ borderRadius: 1.5, height: 36, px: 1.5 }}
              >
                {t('worker.directory.exportExcel', 'Eksporto Excel')}
              </Button>
              {canImport && (
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon sx={{ fontSize: '16px !important' }} />}
                  onClick={() => setImportOpen(true)}
                  sx={{ borderRadius: 1.5, height: 36, px: 1.5 }}
                >
                  {t('worker.import.button', { defaultValue: 'Importo staf' })}
                </Button>
              )}
              {/* «Vërejtje» u hoq nga kjo kokë: lëshimi i një vërejtjeje nuk
                  është veprim i listës së punëtorëve, dhe moduli i vet
                  (`/warnings`) e ka te menuja anësore bashkë me regjistrin. */}
              {/* Një derë e vetme për krijimin e punëtorit.
                  Këtu rrinin dy butona — «Regjistrim (i ri)» dhe «Shto
                  Punëtor» — dhe ai i mbushuri, pra ai që tërheq syrin, çonte te
                  forma e vjetër. `RegisterWorker` është mbi-bashkësi e plotë e
                  `AddWorker`: 21 fusha më shumë (kontrata, banka, leja e punës,
                  sigurimi) dhe asnjë fushë më pak, plus kontrata e caktimi te
                  projekti në të njëjtin hap. Dy butona për të njëjtin veprim e
                  detyronin njeriun të zgjidhte pa e ditur se njëri ishte
                  gjithmonë më i mirë. */}
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                component={Link}
                to="/workerlist/register"
                sx={{ borderRadius: 1.5, height: 36, px: 1.75 }}
              >
                {t('worker.directory.addWorker')}
              </Button>
            </Stack>
          </Stack>

          {/* ── Toolbar: Search + Stat pills + View toggle ───────────────────── */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            spacing={{ xs: 1.5, md: 2 }}
          >
            {/* Search */}
            <TextField
              placeholder={t('worker.directory.searchPlaceholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: { xs: '0 1 auto', md: '0 0 380px' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  fontSize: '0.875rem',
                  minHeight: 40,
                },
                '& .MuiOutlinedInput-input': { py: '8px' },
              }}
            />

            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}
            >
              {/* Stat pills — display-only segmented look */}
              <Stack
                direction="row"
                divider={<Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'divider' }} />}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  overflow: 'hidden',
                  height: 36,
                }}
              >
                {headerStats.map((s) => {
                  const isShown = s.key === 'shown';
                  const isActive = isShown ? statusFilter === 'all' : statusFilter === s.key;
                  const handleClick = () => {
                    if (isShown) { setStatusFilter('all'); return; }
                    setStatusFilter((cur) => (cur === s.key ? 'all' : s.key));
                  };
                  return (
                    <Stack
                      key={s.key}
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      onClick={handleClick}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isActive}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
                      sx={{
                        px: 1.5,
                        height: '100%',
                        cursor: 'pointer',
                        bgcolor: isActive ? toneBg[s.tone] : 'transparent',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { bgcolor: isActive ? toneBg[s.tone] : 'action.hover' },
                        outline: 'none',
                        '&:focus-visible': { boxShadow: (th) => `inset 0 0 0 2px ${alpha(th.palette[s.tone].main, 0.45)}` },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          color: `${s.tone}.main`,
                          lineHeight: 1,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {loading ? '…' : s.value}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? `${s.tone}.main` : 'text.secondary',
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {s.label}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>

              {/* View mode toggle */}
              <Box
                sx={{
                  display: 'flex',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  overflow: 'hidden',
                  height: 36,
                }}
              >
                <Tooltip title="Card view">
                  <IconButton
                    size="small"
                    onClick={() => setViewMode('cards')}
                    sx={{
                      width: 36, height: 34, borderRadius: 0,
                      bgcolor: viewMode === 'cards' ? 'primary.soft' : 'transparent',
                      color: viewMode === 'cards' ? 'primary.main' : 'text.secondary',
                      '&:hover': { bgcolor: viewMode === 'cards' ? 'primary.soft' : 'grey.50' },
                    }}
                  >
                    <ViewModuleIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Table view">
                  <IconButton
                    size="small"
                    onClick={() => setViewMode('table')}
                    sx={{
                      width: 36, height: 34, borderRadius: 0,
                      bgcolor: viewMode === 'table' ? 'primary.soft' : 'transparent',
                      color: viewMode === 'table' ? 'primary.main' : 'text.secondary',
                      '&:hover': { bgcolor: viewMode === 'table' ? 'primary.soft' : 'grey.50' },
                    }}
                  >
                    <ViewListIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </Stack>

          {/* ── Cards view ── */}
          {viewMode === 'cards' && (
            <>
            <Box>
              {!loading && !statusFiltered.length && (
                <Box sx={{ textAlign: 'center', py: 8, color: 'text.disabled' }}>
                  <GroupsIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                  <Typography variant="body2" color="text.disabled">{t('worker.directory.noEmployees')}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)', lg: 'repeat(4,1fr)', xl: 'repeat(5,1fr)' }, gap: 2 }}>
                {(loading ? Array.from({ length: 8 }) : statusFiltered).map((w, idx) => {
                  const worker = w || {};
                  const contract = getPrimaryContract(worker);
                  const { startDate } = getWorkerDates(worker, contract);
                  const deptObj = departments.find(d => String(d.Id || d.id) === String(worker.Department));
                  const deptName = deptObj?.Name || deptObj?.name || null;
                  const deptIdx = deptObj ? departments.indexOf(deptObj) : -1;
                  // Pa departament → gri neutrale, jo ngjyra e departamentit të parë.
                  // Mungesa e lidhjes duhet të lexohet si mungesë; me indigo,
                  // një punëtor pa departament dukej pjesë e njërit prej tyre.
                  const deptPalette = deptIdx >= 0 ? DEPT_PALETTE[deptIdx % DEPT_PALETTE.length] : { bg: '#F1F5F9', fg: '#64748B' };
                  const position = worker?.Position || contract?.Position || '—';
                  const name = [worker?.FirstName, worker?.LastName].filter(Boolean).join(' ') || null;
                  const initials = name ? name.split(' ').map(x => x[0]).join('').slice(0,2).toUpperCase() : '?';
                  const isInactive = worker?.Active === false || worker?.active === false;
                  const isOnLeave = false; // extend if you have leave status
                  const photoSrc = worker?.Image || null;

                  const statusTone = isInactive ? 'warning' : isOnLeave ? 'info' : 'success';
                  const statusLabel = isInactive
                    ? t('worker.directory.statusTerminated')
                    : isOnLeave
                      ? t('worker.directory.statusOnLeave')
                      : t('worker.directory.statusActive');

                  return (
                    <Card
                      key={loading ? `skeleton-${idx}` : worker?.Id}
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        overflow: 'visible',
                        position: 'relative',
                        opacity: isInactive ? 0.85 : 1,
                        transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(17,24,39,0.06), 0 2px 6px rgba(17,24,39,0.04)',
                          borderColor: 'grey.300',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        {/* Top row: status chip (+ consent chip) on the left, menu on the right.
                            The consent chip shares this row rather than taking one of its own:
                            on its own line it made terminated cards taller than the rest and
                            broke the grid's alignment. */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0.5 }}>
                          {loading ? (
                            <Skeleton variant="rounded" width={64} height={22} />
                          ) : (
                            <Chip
                              label={statusLabel}
                              size="small"
                              color={statusTone}
                              sx={{ height: 22, fontWeight: 600, fontSize: '0.6875rem' }}
                            />
                          )}

                          {/* Consent badge for terminated workers */}
                          {isInactive && !loading && (() => {
                            const cs = consentMap[String(worker?.Id)];
                            if (!cs) return null;
                            const cfg = cs.status === 'agreed'
                              ? { label: `✓ ${t('worker.consent.agreed')}`, color: 'success' }
                              : cs.status === 'disagreed'
                              ? { label: `✗ ${t('worker.consent.disagreed')}`, color: 'error' }
                              : { label: `⏳ ${t('worker.consent.pending')}`, color: 'warning' };
                            return (
                              <Chip
                                label={cfg.label}
                                size="small"
                                color={cfg.color}
                                onClick={() => setConsentDialogData(cs)}
                                sx={{ height: 22, fontWeight: 600, fontSize: '0.6875rem', cursor: 'pointer' }}
                              />
                            );
                          })()}
                          </Stack>
                          <IconButton
                            size="small"
                            aria-label={t('worker.actions.moreOptions', 'Më shumë opsione')}
                            sx={{ width: 28, height: 28 }}
                            onClick={(e) => openCardMenu(e, worker)}
                          >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Stack>

                        {/* Avatar + name + role */}
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                          {loading ? (
                            <Skeleton variant="circular" width={48} height={48} />
                          ) : (
                            <Box
                              sx={{
                                position: 'relative',
                                cursor: 'pointer',
                                '&:hover .photo-edit-overlay': { opacity: 1 },
                              }}
                              onClick={() => worker?.Id && openPhotoDialog(worker)}
                            >
                              <Avatar
                                src={photoSrc}
                                sx={{
                                  width: 48, height: 48,
                                  fontSize: '0.9375rem', fontWeight: 600,
                                  bgcolor: deptPalette.bg,
                                  color: deptPalette.fg,
                                  border: 0,
                                }}
                              >
                                {!photoSrc && initials}
                              </Avatar>
                              {/* Edit overlay on hover only — no permanent badge */}
                              <Box
                                className="photo-edit-overlay"
                                sx={{
                                  position: 'absolute', inset: 0,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(17,24,39,0.45)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  opacity: 0,
                                  transition: 'opacity 0.15s ease',
                                }}
                              >
                                <CameraAltIcon sx={{ fontSize: 16, color: '#fff' }} />
                              </Box>
                            </Box>
                          )}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              noWrap
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                color: 'text.primary',
                                letterSpacing: '-0.01em',
                                lineHeight: 1.25,
                              }}
                            >
                              {loading ? <Skeleton width={110} /> : (name || '—')}
                            </Typography>
                            <Typography
                              noWrap
                              sx={{
                                fontSize: '0.8125rem',
                                color: 'text.secondary',
                                fontWeight: 500,
                                lineHeight: 1.4,
                                mt: 0.25,
                              }}
                            >
                              {loading ? <Skeleton width={90} /> : (position !== '—' ? position : worker?.Qualification || '—')}
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Info grid: Department + Joined */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                          <Box>
                            <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.625rem', display: 'block', lineHeight: 1.4 }}>
                              {t('worker.directory.department', 'Department')}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary', mt: 0.25 }}>
                              {loading ? <Skeleton width={70} /> : (deptName || '—')}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.625rem', display: 'block', lineHeight: 1.4 }}>
                              {t('worker.directory.joinedOn', 'Joined')}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary', mt: 0.25 }}>
                              {loading ? <Skeleton width={70} /> : startDate}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Contact info — minimal, no divider */}
                        <Stack spacing={0.75} sx={{ mb: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <EmailIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                            <Typography noWrap sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                              {loading ? <Skeleton width={130} /> : (worker?.Email || '—')}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CallIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                            <Typography noWrap sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                              {loading ? <Skeleton width={100} /> : (worker?.Phone || '—')}
                            </Typography>
                          </Stack>
                        </Stack>

                        {/* Action buttons */}
                        {!loading && worker?.Id && (
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              component={Link}
                              to={`/workerlist/editworker/${worker.Id}`}
                              sx={{ flex: 1, height: 36 }}
                            >
                              {t('worker.actions.edit', 'Edit')}
                            </Button>
                            <Button
                              variant="contained"
                              component={Link}
                              to={`/workerlist/worker/${worker.Id}`}
                              sx={{ flex: 1, height: 36 }}
                            >
                              {t('worker.actions.view', 'View')}
                            </Button>
                          </Stack>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>

            {/* ── Card 3-dot dropdown menu ── */}
            <Menu
              anchorEl={cardMenu?.anchorEl}
              open={Boolean(cardMenu)}
              onClose={closeCardMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { minWidth: 190, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', mt: 0.5 } }}
            >
              {/* View */}
              {cmWorker?.Id && (
                <MenuItem component={Link} to={`/workerlist/worker/${cmWorker.Id}`} onClick={closeCardMenu}
                  sx={{ gap: 1.5, py: 1.1 }}>
                  <ListItemIcon sx={{ minWidth: 0 }}><VisibilityIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Shiko detajet</ListItemText>
                </MenuItem>
              )}
              {cmWorker?.Id && (
                <MenuItem component={Link} to={`/workerlist/editworker/${cmWorker.Id}`} onClick={closeCardMenu}
                  sx={{ gap: 1.5, py: 1.1 }}>
                  <ListItemIcon sx={{ minWidth: 0 }}><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{t('worker.actions.edit')}</ListItemText>
                </MenuItem>
              )}
              {/* Email */}
              {cmWorker?.Email && (
                <MenuItem onClick={() => { openEmailDialog(cmWorker); closeCardMenu(); }} sx={{ gap: 1.5, py: 1.1 }}>
                  <ListItemIcon sx={{ minWidth: 0 }}><EmailIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{t('worker.actions.sendEmail')}</ListItemText>
                </MenuItem>
              )}
              {/* Call */}
              {cmWorker?.Phone && (
                <MenuItem component="a" href={`tel:${cmWorker.Phone}`} onClick={closeCardMenu} sx={{ gap: 1.5, py: 1.1 }}>
                  <ListItemIcon sx={{ minWidth: 0 }}><CallIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{t('worker.actions.call')}</ListItemText>
                </MenuItem>
              )}
              {/* Terminate */}
              {cmWorker?.Id && canTerminate && !(cmWorker?.Active === false || cmWorker?.active === false) && (
                <MenuItem onClick={() => { openTerminate(cmWorker); closeCardMenu(); }} sx={{ gap: 1.5, py: 1.1, color: '#dc2626' }}>
                  <ListItemIcon sx={{ minWidth: 0 }}><BlockIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2', color: '#dc2626' }}>{t('worker.actions.terminateContract')}</ListItemText>
                </MenuItem>
              )}
              {/* Reactivate */}
              {cmWorker?.Id && canReactivate && (cmWorker?.Active === false || cmWorker?.active === false) && (
                <MenuItem onClick={() => { openReactivate(cmWorker); closeCardMenu(); }} sx={{ gap: 1.5, py: 1.1, color: '#10b981' }}>
                  <ListItemIcon sx={{ minWidth: 0 }}><RestoreIcon fontSize="small" sx={{ color: '#10b981' }} /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2', color: '#10b981' }}>{t('worker.actions.reactivate')}</ListItemText>
                </MenuItem>
              )}
            </Menu>
            </>
          )}

          {/* ── Table ── */}
          {viewMode === 'table' && (
          <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-head': {
                      py: '7px', fontWeight: 700, fontSize: '0.68rem',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      color: 'text.disabled', borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      backgroundClip: 'padding-box',
                    },
                  }}>
                    <TableCell>{t('worker.table.employee')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('worker.table.positionDept')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('worker.table.contract')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{t('worker.table.personalId')}</TableCell>
                    <TableCell>{t('worker.table.atk')}</TableCell>
                    {customFieldDefs.map(f => (
                      <TableCell key={f.key} sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{f.label}</TableCell>
                    ))}
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(loading ? Array.from({ length: 8 }) : statusFiltered).map((w, idx) => {
                    const worker = w || {};
                    const contract = getPrimaryContract(worker);
                    const { startDate, endDate } = getWorkerDates(worker, contract);
                    const deptObj = departments.find(d => String(d.Id || d.id) === String(worker.Department));
                    const deptName  = deptObj?.Name || deptObj?.name || null;
                    const deptIdx = deptObj ? departments.indexOf(deptObj) : -1;
                  // Pa departament → gri neutrale, jo ngjyra e departamentit të parë.
                  // Mungesa e lidhjes duhet të lexohet si mungesë; me indigo,
                  // një punëtor pa departament dukej pjesë e njërit prej tyre.
                  const deptPalette = deptIdx >= 0 ? DEPT_PALETTE[deptIdx % DEPT_PALETTE.length] : { bg: '#F1F5F9', fg: '#64748B' };
                    const position  = worker?.Position || contract?.Position || '—';
                    const name = [worker?.FirstName, worker?.LastName].filter(Boolean).join(' ') || null;
                    const initials = name ? name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase() : '?';
                    const isATKOk = worker?.ATKRegistered === true || worker?.ATKRegistered === 1 || worker?.ATKRegistered === '1' || worker?.ATKRegistered === 'true';
                    const isInactive = worker?.Active === false || worker?.active === false;

                    return (
                      <TableRow
                        hover
                       key={loading ? `skeleton-${idx}` : worker?.Id}
                        sx={{
                          opacity: isInactive ? 0.6 : 1,
                          bgcolor: isInactive ? (themeArg) => themeArg.palette.mode === 'dark' ? 'rgba(220,38,38,0.04)' : '#fff8f8' : undefined,
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.025) },
                          '&:hover .row-photo-edit': { opacity: 1 },
                          '&:last-child td, &:last-child th': { borderBottom: 0 },
                        }}
                      >

                        {/* Punonjësi */}
                        <TableCell sx={{ py: '7px' }}>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box sx={{ position: 'relative', flexShrink: 0 }} onClick={() => !loading && worker?.Id && openPhotoDialog(worker)}>
                              <Avatar
                                src={worker?.Image || undefined}
                                sx={{ width: 30, height: 30, bgcolor: deptPalette.bg, color: deptPalette.fg, fontSize: '0.7rem', fontWeight: 700, cursor: worker?.Id ? 'pointer' : 'default' }}
                              >
                                {!worker?.Image && initials}
                              </Avatar>
                              {!loading && worker?.Id && (
                                <Box className="row-photo-edit" sx={{
                                  position: 'absolute', bottom: -1, right: -1,
                                  width: 13, height: 13, borderRadius: '50%',
                                  bgcolor: '#0891B2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  border: '1.5px solid #fff', cursor: 'pointer',
                                  opacity: 0, transition: 'opacity 0.15s',
                                }}>
                                  <CameraAltIcon sx={{ fontSize: 7, color: '#fff' }} />
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={700} noWrap sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
                                {name || (loading ? <Skeleton width={110} /> : '—')}
                              </Typography>
                              <Typography color="text.disabled" noWrap sx={{ fontSize: '0.69rem', lineHeight: 1.3 }}>
                                {worker?.Email || (loading ? <Skeleton width={120} /> : worker?.Phone || '—')}
                              </Typography>
                              {isInactive && !loading && (
                                <Box component="span" sx={{ display:'inline-flex', alignItems:'center', gap:0.3, px:0.7, py:'1px', borderRadius:999, fontSize:'0.62rem', fontWeight:700, bgcolor:'#fee2e2', color:'#dc2626', border:'1px solid #fca5a5', mt:0.2 }}>
                                  <BlockIcon sx={{ fontSize:9 }} /> Joaktiv
                                </Box>
                              )}
                              {isInactive && !loading && (() => {
                                const cs = consentMap[String(worker?.Id)];
                                if (!cs) return null;
                                const cfg = cs.status === 'agreed'
                                  ? { label: '✓ Pajtos', bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' }
                                  : cs.status === 'disagreed'
                                  ? { label: '✗ Nuk pajtos', bg: '#fef2f2', color: '#991b1b', border: '#fecaca' }
                                  : { label: '⏳ Pritje', bg: '#fffbeb', color: '#92400e', border: '#fde68a' };
                                return (
                                  <Box component="span" onClick={() => setConsentDialogData(cs)} sx={{ cursor:'pointer', display:'inline-flex', alignItems:'center', gap:0.3, px:0.7, py:'1px', borderRadius:999, fontSize:'0.62rem', fontWeight:700, bgcolor:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}`, mt:0.2, ml:0.5, '&:hover':{opacity:0.8} }}>
                                    {cfg.label}
                                  </Box>
                                );
                              })()}
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Pozita & Departamenti */}
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, py: '7px' }}>
                          <Typography fontWeight={600} noWrap sx={{ fontSize: '0.79rem', lineHeight: 1.3 }}>
                            {loading ? <Skeleton width={100} /> : position}
                          </Typography>
                          {deptName && !loading && (
                            <Box component="span" sx={{
                              display: 'inline-block', px: 0.9, py: '1px',
                              borderRadius: 999, fontSize: '0.66rem', fontWeight: 600,
                              bgcolor: deptPalette.bg, color: deptPalette.fg, lineHeight: 1.6,
                            }}>
                              {deptName}
                            </Box>
                          )}
                          {worker?.SupervisorName && !loading && (
                            <Typography noWrap sx={{ fontSize: '0.63rem', color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }}>
                              {t('worker.supervisor')}: {worker.SupervisorName}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Kontrata */}
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, py: '7px' }}>
                          {loading ? <Skeleton width={110} /> : (
                            <Stack spacing={0}>
                              <Typography fontWeight={600} sx={{ fontSize: '0.72rem', lineHeight: 1.4 }}>{startDate}</Typography>
                              {endDate && endDate !== '—'
                                ? <Typography color="error.main" sx={{ fontSize: '0.69rem', lineHeight: 1.4 }}>→ {endDate}</Typography>
                                : <Typography color="success.main" sx={{ fontSize: '0.69rem', lineHeight: 1.4 }}>Pa afat</Typography>
                              }
                            </Stack>
                          )}
                        </TableCell>

                        {/* Nr. Personal */}
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, py: '7px' }}>
                          <Typography color="text.secondary" sx={{ fontSize: '0.71rem', fontFamily: 'monospace' }}>
                            {loading ? <Skeleton width={90} /> : (worker?.PersonalId || '—')}
                          </Typography>
                        </TableCell>

                        {/* ATK */}
                        <TableCell sx={{ py: '7px' }}>
                          {loading ? <Skeleton variant="rounded" width={72} height={20} /> :
                            worker?.ATKRegisteredAt ? (
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.35,
                                px: 0.9, py: '2px', borderRadius: 999,
                                fontSize: '0.67rem', fontWeight: 600,
                                bgcolor: isATKOk ? '#d1fae5' : '#fef3c7',
                                color: isATKOk ? '#065f46' : '#92400e',
                                border: '1px solid',
                                borderColor: isATKOk ? '#a7f3d0' : '#fde68a',
                              }}>
                                <CheckCircleIcon sx={{ fontSize: 10 }} />
                                {isATKOk ? dateFormats(worker.ATKRegisteredAt) : 'I pakonfirmuar'}
                              </Box>
                            ) : (
                              <Box
                                component="span"
                                onClick={() => openReg(worker)}
                                sx={{
                                  display: 'inline-flex', alignItems: 'center',
                                  px: 0.9, py: '2px', borderRadius: 999,
                                  fontSize: '0.67rem', fontWeight: 600,
                                  bgcolor: '#fee2e2', color: '#991b1b',
                                  border: '1px solid #fca5a5',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: '#fecaca' },
                                }}
                              >
                                Regjistro
                              </Box>
                            )
                          }
                        </TableCell>

                        {/* Custom field columns */}
                        {customFieldDefs.map(f => {
                          const cfVals = customFieldValuesMap[String(worker?.Id || worker?.id)] || {};
                          return (
                            <TableCell key={f.key} sx={{ py: '7px', display: { xs: 'none', xl: 'table-cell' }, fontSize: '0.78rem' }}>
                              {loading ? <Skeleton width={80} /> : formatCustomFieldValue(f, cfVals[f.key])}
                            </TableCell>
                          );
                        })}

                        {/* Veprime */}
                        <TableCell align="right" sx={{ py: '7px' }}>
                          <Stack direction="row" spacing={0.2} justifyContent="flex-end">
                            {worker?.Id && (
                              <Tooltip title="Shiko detajet">
                                <IconButton size="small" component={Link} to={`/workerlist/worker/${worker.Id}`} sx={{ p: '3px', color: 'text.secondary' }}>
                                  <VisibilityIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {worker?.Email && (
                              <Tooltip title={t('worker.actions.sendEmail')}>
                                <IconButton size="small" onClick={() => openEmailDialog(worker)} sx={{ p: '3px' }}>
                                  <EmailIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {worker?.Phone && (
                              <Tooltip title="Thirr">
                                <IconButton size="small" component="a" href={`tel:${worker.Phone}`} sx={{ p: '3px' }}>
                                  <CallIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {worker?.Id && (
                              <Tooltip title="Ndrysho">
                                <IconButton size="small" component={Link} to={`/workerlist/editworker/${worker.Id}`} color="primary" sx={{ p: '3px' }}>
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {worker?.Id && canTerminate && !isInactive && (
                              <Tooltip title={t('worker.actions.terminateContract')}>
                                <IconButton size="small" sx={{ p: '3px', color: '#dc2626' }} onClick={() => openTerminate(worker)}>
                                  <BlockIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {worker?.Id && canReactivate && isInactive && (
                              <Tooltip title={t('worker.actions.reactivate')}>
                                <IconButton size="small" sx={{ p: '3px', color: '#10b981' }} onClick={() => openReactivate(worker)}>
                                  <RestoreIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {worker?.Id && (
                              <Tooltip title="Fshi">
                                <IconButton size="small" color="error" onClick={() => confirmDelete(worker)} sx={{ p: '3px' }}>
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>

                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && !statusFiltered.length && (
              <Box sx={{ textAlign: 'center', py: 5, color: 'text.disabled' }}>
                <GroupsIcon sx={{ fontSize: 32, mb: 0.75, opacity: 0.35 }} />
                <Typography variant="body2" color="text.disabled">{t('worker.directory.noEmployees')}</Typography>
              </Box>
            )}
          </Paper>
          )}

        </Stack>
      </Box>
    <Dialog open={emailDialogOpen} onClose={closeEmailDialog} fullWidth maxWidth="sm">
      <DialogTitle>{t('worker.dialogs.sendRegistrationEmail')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {emailError ? <Alert severity="error">{emailError}</Alert> : null}
          {emailSuccess ? <Alert severity="success">{emailSuccess}</Alert> : null}
          <TextField
            label={t('worker.dialogs.registeredEmail')}
            value={emailWorker?.Email || ''}
            InputProps={{ readOnly: true }}
            fullWidth
          />
          <TextField
            label={t('worker.dialogs.secondaryEmailLabel')}
            value={secondaryEmail}
            onChange={(e) => setSecondaryEmail(e.target.value)}
            placeholder={t('worker.dialogs.emailPlaceholder')}
            helperText={t('worker.dialogs.secondaryEmailHelper')}
            fullWidth
          />
          <TextField
            label={t('worker.dialogs.subject')}
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('worker.dialogs.content')}
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            helperText={t('worker.dialogs.contentHelper')}
            multiline
            minRows={5}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeEmailDialog} disabled={sendingEmail}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSendEmail}
          variant="contained"
          disabled={sendingEmail}
        >
          {sendingEmail ? t('worker.dialogs.sending') : t('worker.dialogs.sendEmail')}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={regOpen} onClose={closeReg} fullWidth maxWidth="sm">
      <DialogTitle>{t('worker.dialogs.registerAtk')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('worker.dialogs.workerLabel')}: <b>{regWorker?.FirstName} {regWorker?.LastName}</b>
          </Typography>
          <TextField
            label={t('worker.dialogs.registrationDate')}
            type="date"
            value={regDate}
            onChange={(e) => setRegDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label={t('worker.dialogs.commentOptional')}
            value={regComment}
            onChange={(e) => setRegComment(e.target.value)}
            multiline minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeReg} disabled={saving}>{t('common.cancel')}</Button>
        <Button onClick={saveReg} variant="contained" disabled={saving}>{t('common.save')}</Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={Boolean(deleteTarget)}
      onClose={cancelDelete}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 24px 60px rgba(15, 23, 42, .18)',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 1, display: 'flex', alignItems: 'flex-start', gap: 1.75 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '12px',
          bgcolor: '#FEF2F2', color: '#DC2626',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <DeleteForeverOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, pt: 0.25 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
            {t('worker.dialogs.deleteWorker')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748B', mt: 0.375, lineHeight: 1.5 }}>
            Ky veprim nuk mund të zhbëhet.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, pb: 2.5 }}>
        <Box sx={{
          mt: 1.5, p: 1.75, borderRadius: '10px',
          bgcolor: '#F8FAFC',
          border: '1px solid #E2E8F0',
        }}>
          <Typography sx={{ fontSize: 13.5, color: '#334155', lineHeight: 1.55 }}>
            {t('worker.dialogs.deleteConfirmation')}{' '}
            <Box component="strong" sx={{ color: '#0F172A', fontWeight: 700 }}>
              {deleteTarget?.FirstName} {deleteTarget?.LastName}
            </Box>?
          </Typography>
          {deleteTarget?.Email && (
            <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.5, fontFamily: 'ui-monospace, monospace' }}>
              {deleteTarget.Email}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{
        px: 3, py: 2,
        borderTop: '1px solid #EEF2F6',
        bgcolor: '#FAFBFC',
        display: 'flex', justifyContent: 'flex-end', gap: 1,
      }}>
        <Button
          onClick={cancelDelete}
          disabled={deleting}
          sx={{
            height: 36, px: 1.75, borderRadius: '8px',
            color: '#334155', bgcolor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            textTransform: 'none', fontSize: 13, fontWeight: 500,
            '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
            '&.Mui-disabled': { color: '#CBD5E1' },
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleDelete}
          disabled={deleting}
          disableElevation
          startIcon={deleting
            ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
            : <DeleteForeverOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 36, px: 1.75, borderRadius: '8px',
            bgcolor: '#DC2626', color: '#FFFFFF',
            textTransform: 'none', fontSize: 13, fontWeight: 600,
            '&:hover': { bgcolor: '#B91C1C' },
            '&.Mui-disabled': { bgcolor: '#F1F5F9', color: '#94A3B8' },
          }}
        >
          {deleting ? t('worker.dialogs.deleting') : t('common.delete')}
        </Button>
      </Box>
    </Dialog>

    {/* ── Dialog: Consent Details ── */}
    <Dialog open={Boolean(consentDialogData)} onClose={() => setConsentDialogData(null)} fullWidth maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      {consentDialogData && (() => {
        const cs = consentDialogData;
        const isAgreed = cs.status === 'agreed';
        const isPending = cs.status === 'pending';
        const statusCfg = isAgreed
          ? { label: t('worker.consent.agreedStatus'), color: '#166534', bg: '#f0fdf4', border: '#bbf7d0', icon: '✓' }
          : isPending
          ? { label: t('worker.consent.pendingStatus'), color: '#92400e', bg: '#fffbeb', border: '#fde68a', icon: '⏳' }
          : { label: t('worker.consent.disagreedStatus'), color: '#991b1b', bg: '#fef2f2', border: '#fecaca', icon: '✗' };
        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('sq-AL', { day:'2-digit', month:'long', year:'numeric' }) : '—';
        return (<>
          <Box sx={{ background: 'linear-gradient(135deg,#1e293b,#334155)', px:3, py:1.75, display:'flex', alignItems:'center', gap:1.5 }}>
            <BlockIcon sx={{ color:'#dc2626', fontSize:18 }} />
            <Box>
              <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'0.9rem', lineHeight:1.2 }}>{t('worker.consent.title')}</Typography>
              <Typography sx={{ color:'rgba(255,255,255,0.6)', fontSize:'0.72rem' }}>{cs.workerName}</Typography>
            </Box>
          </Box>
          <DialogContent sx={{ p:2.5 }}>
            <Box sx={{ bgcolor:statusCfg.bg, border:`1px solid ${statusCfg.border}`, borderRadius:1.5, p:1.5, mb:2, display:'flex', alignItems:'center', gap:1 }}>
              <Typography sx={{ fontSize:'1rem' }}>{statusCfg.icon}</Typography>
              <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:statusCfg.color }}>{statusCfg.label}</Typography>
            </Box>
            <Stack spacing={0}>
              {[
                [t('worker.consent.worker'), cs.workerName],
                [t('worker.consent.terminationDate'), fmtDate(cs.termDate)],
                [t('worker.consent.sent'), fmtDate(cs.createdAt)],
                ...(cs.respondedAt ? [[t('worker.consent.responded'), fmtDate(cs.respondedAt)]] : []),
              ].map(([lbl, val], i, arr) => (
                <Box key={lbl} sx={{ display:'flex', borderBottom: i<arr.length-1?'1px solid #f1f5f9':'none' }}>
                  <Typography sx={{ width:'45%', px:1.5, py:0.9, fontSize:'0.75rem', color:'text.secondary', fontWeight:600, bgcolor:'#f8fafc' }}>{lbl}</Typography>
                  <Typography sx={{ flex:1, px:1.5, py:0.9, fontSize:'0.78rem', color:'text.primary' }}>{val}</Typography>
                </Box>
              ))}
            </Stack>
            {cs.comment && (
              <Box sx={{ mt:2 }}>
                <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:'text.secondary', textTransform:'uppercase', letterSpacing:0.5, mb:0.75 }}>{t('worker.consent.commentStatement')}</Typography>
                <Box sx={{ bgcolor:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:1.5, p:1.5, borderLeft:'3px solid #374151' }}>
                  <Typography sx={{ fontSize:'0.82rem', color:'#374151', lineHeight:1.65, whiteSpace:'pre-wrap' }}>{cs.comment}</Typography>
                </Box>
              </Box>
            )}
            {isPending && (
              <Box sx={{ mt:2, p:1.25, bgcolor:'#fffbeb', border:'1px solid #fde68a', borderRadius:1.5 }}>
                <Typography sx={{ fontSize:'0.75rem', color:'#92400e' }}>{t('worker.consent.noPendingResponse')}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px:2.5, py:1.5, borderTop:'1px solid #f1f5f9' }}>
            <Button onClick={() => setConsentDialogData(null)} size="small" sx={{ textTransform:'none', color:'#6b7280', fontWeight:500 }}>{t('common.close')}</Button>
          </DialogActions>
        </>);
      })()}
    </Dialog>

    {/* ── Dialog: Ndërpre Kontratën ── */}
    <Dialog
      open={Boolean(terminateTarget)}
      onClose={() => !terminateSaving && setTerminateTarget(null)}
      fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      {/* Compact branded header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
        px: 3, py: 1.75,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BlockIcon sx={{ color: '#fff', fontSize: 20 }} />
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
              {t('worker.dialogs.terminateTitle')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', lineHeight: 1 }}>
              {terminateTarget?.FirstName} {terminateTarget?.LastName}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.68rem' }}>
            {terminateTarget?.Position || terminateTarget?.Department || ''}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Stack spacing={1.75}>

          {/* ── Balanca pushimit ── */}
          {terminateLoading ? (
            <Box sx={{ display:'flex', alignItems:'center', gap:1, py: 0.5 }}>
              <CircularProgress size={14} sx={{ color: '#b91c1c' }} />
              <Typography variant="caption" color="text.secondary">{t('worker.dialogs.checkingLeaves')}</Typography>
            </Box>
          ) : terminateLeave ? (
            <Box sx={{
              border: '1px solid',
              borderColor: terminateLeave.availableDays > 0 ? '#fbbf24' : '#bbf7d0',
              borderRadius: 1.5,
              p: 1.5,
              bgcolor: terminateLeave.availableDays > 0 ? '#fffbeb' : '#f0fdf4',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <WarningAmberIcon sx={{ fontSize: 15, color: terminateLeave.availableDays > 0 ? '#d97706' : '#16a34a' }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: terminateLeave.availableDays > 0 ? '#92400e' : '#166534' }}>
                  {terminateLeave.typeName || t('worker.leave.annual')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[
                  [t('worker.leave.total'), terminateLeave.totalDays],
                  [t('worker.leave.used'), terminateLeave.usedDays],
                  [t('worker.leave.pending'), terminateLeave.pendingDays],
                  [t('worker.leave.remaining'), terminateLeave.availableDays],
                ].map(([lbl, val]) => (
                  <Box key={lbl}>
                    <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af', lineHeight: 1 }}>{lbl}</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: lbl === t('worker.leave.remaining') && val > 0 ? '#d97706' : '#111827' }}>
                      {val} {t('common.days')}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {terminateLeave.availableDays > 0 && (
                <Typography sx={{ fontSize: '0.72rem', color: '#92400e', mt: 0.75 }}>
                  {t('worker.dialogs.unusedLeavesWarning', { count: terminateLeave.availableDays })}
                </Typography>
              )}
            </Box>
          ) : null}

          {/* ── Data + Arsyeja (row) ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 1.5 }}>
            <TextField
              label={t('worker.dialogs.terminationDate')}
              type="date"
              value={terminateDate}
              onChange={(e) => setTerminateDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>{t('worker.dialogs.terminationReason')}</InputLabel>
              <Select
                label={t('worker.dialogs.terminationReason')}
                value={terminateSelectedId ?? ''}
                onChange={(e) => {
                  const tpl = terminateTemplates.find(t => t.id === e.target.value);
                  if (tpl) {
                    setTerminateSelectedId(tpl.id);
                    const raw = tpl.comment || tpl.label;
                    setTerminateComment(fillDecisionPlaceholders(raw, { worker: terminateTarget, date: terminateDate, layout: decisionCtx.layout, company: decisionCtx.company }));
                  }
                }}
                displayEmpty
                renderValue={(val) => {
                  if (!val && val !== 0) return <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>{t('common.select')}</span>;
                  const tpl = terminateTemplates.find(t => t.id === val);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#dc2626', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b91c1c' }}>{tpl?.label}</Typography>
                    </Box>
                  );
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: terminateSelectedId != null ? '#dc2626' : undefined },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#dc2626' },
                }}
                MenuProps={{ PaperProps: { sx: { borderRadius: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', mt: 0.5, maxHeight: 240 } } }}
              >
                {terminateTemplates.map((tpl) => (
                  <MenuItem key={tpl.id} value={tpl.id} sx={{
                    fontSize: '0.82rem', py: 0.9, px: 2,
                    '&.Mui-selected': { bgcolor: '#fef2f2', color: '#b91c1c', fontWeight: 700 },
                    '&.Mui-selected:hover': { bgcolor: '#fef2f2' },
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}>
                    {tpl.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* ── Vendimi / Komenti ── */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('worker.dialogs.decisionText')}
              </Typography>
              {terminateComment && (
                <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af' }}>
                  {terminateComment.length} karaktere
                </Typography>
              )}
            </Box>
            <TextField
              value={terminateComment}
              onChange={(e) => { setTerminateComment(e.target.value); setTerminateSelectedId(null); }}
              multiline
              minRows={terminateComment ? 6 : 3}
              fullWidth
              size="small"
              placeholder={t('worker.dialogs.decisionPlaceholder')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.82rem',
                  lineHeight: 1.65,
                  fontFamily: terminateComment ? '"Georgia", serif' : 'inherit',
                },
              }}
            />
          </Box>

          {/* ── Warning notice ── */}
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1,
            bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1.5, p: 1.25,
          }}>
            <BlockIcon sx={{ fontSize: 15, color: '#dc2626', mt: 0.2, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.76rem', color: '#7f1d1d', lineHeight: 1.55 }}>
              {t('worker.dialogs.terminateWarning')}
            </Typography>
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #f1f5f9', gap: 1 }}>
        <Button
          onClick={() => setTerminateTarget(null)}
          disabled={terminateSaving}
          size="small"
          sx={{ color: '#6b7280', textTransform: 'none', fontWeight: 500 }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={confirmTerminate}
          disabled={terminateSaving}
          size="small"
          startIcon={terminateSaving ? <CircularProgress size={13} color="inherit" /> : <BlockIcon sx={{ fontSize: 15 }} />}
          sx={{
            background: 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
            textTransform: 'none', fontWeight: 600, px: 2.5, borderRadius: 1.5,
            boxShadow: '0 2px 8px rgba(185,28,28,0.35)',
            '&:hover': { background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)' },
          }}
        >
          {terminateSaving ? t('worker.dialogs.terminating') : t('worker.dialogs.confirmTermination')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* ── Dialog: Foto e profilit ── */}
    <Dialog open={photoDialogOpen} onClose={closePhotoDialog} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <AddPhotoAlternateIcon sx={{ color: '#0891B2', fontSize: 22 }} />
        {t('worker.dialogs.profilePhoto')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Preview */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Avatar
              src={photoPickerImage || undefined}
              sx={{ width: 80, height: 80, bgcolor: '#3E8FA3', fontSize: '1.5rem', fontWeight: 700, border: '3px solid', borderColor: 'primary.light' }}
            >
              {!photoPickerImage && (() => {
                const n = [photoWorker?.FirstName, photoWorker?.LastName].filter(Boolean).join(' ');
                return n ? n.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase() : '?';
              })()}
            </Avatar>
            <Typography variant="caption" color="text.secondary">
              {photoWorker?.FirstName} {photoWorker?.LastName}
            </Typography>
          </Box>

          {/* Upload button */}
          <Box>
            <input accept="image/*" style={{ display: 'none' }} id="photoFileInput" type="file" onChange={handlePhotoFile} />
            <label htmlFor="photoFileInput">
              <Button
                component="span"
                variant="outlined"
                size="small"
                startIcon={<AddPhotoAlternateIcon sx={{ fontSize: 16 }} />}
                fullWidth
                sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.8rem' }}
              >
                {t('worker.dialogs.uploadPhoto')}
              </Button>
            </label>
          </Box>

          {/* Placeholder grid */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem' }}>
              {t('worker.dialogs.selectAvatar')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.75 }}>
              {PLACEHOLDER_AVATARS.map((av, idx) => (
                <Tooltip key={av.label} title={av.label} arrow>
                  <Box
                    onClick={() => setPhotoPickerImage(av.src)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: photoPickerImage === av.src ? 'primary.main' : 'transparent',
                      p: '2px',
                      transition: 'border-color 0.15s',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Avatar src={av.src} sx={{ width: 34, height: 34 }} />
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>

          {photoPickerImage && (
            <Button
              size="small"
              color="error"
              variant="text"
              onClick={() => setPhotoPickerImage('')}
              sx={{ textTransform: 'none', fontSize: '0.75rem', alignSelf: 'flex-start' }}
            >
              {t('worker.dialogs.removePhoto')}
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closePhotoDialog} disabled={photoSaving} size="small">{t('common.cancel')}</Button>
        <Button
          onClick={saveWorkerPhoto}
          variant="contained"
          disabled={photoSaving}
          size="small"
          sx={{
            borderRadius: '8px', textTransform: 'none',
            background: 'linear-gradient(135deg, #3E8FA3 0%, #0891B2 100%)',
            boxShadow: 'none',
          }}
          startIcon={photoSaving ? <CircularProgress size={13} color="inherit" /> : null}
        >
          {photoSaving ? t('worker.dialogs.saving') : t('worker.dialogs.savePhoto')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* ── Dialog: Reaktivizo ── */}
    <Dialog open={Boolean(reactivateTarget)} onClose={() => !reactivateSaving && setReactivateTarget(null)}>
      <DialogTitle sx={{ display:'flex', alignItems:'center', gap:1 }}>
        <RestoreIcon sx={{ color:'#10b981', fontSize:20 }} />
        {t('worker.dialogs.reactivateTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('worker.dialogs.reactivateConfirmation')}{' '}
          <b>{reactivateTarget?.FirstName} {reactivateTarget?.LastName}</b>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('worker.dialogs.reactivateNote')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReactivateTarget(null)} disabled={reactivateSaving}>{t('common.cancel')}</Button>
        <Button
          onClick={confirmReactivate}
          color="success"
          variant="contained"
          disabled={reactivateSaving}
          startIcon={reactivateSaving ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon />}
        >
          {reactivateSaving ? t('worker.dialogs.reactivating') : t('worker.dialogs.reactivate')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* ── Dialog: Importo staf ── */}
    <StaffImportDialog
      open={importOpen}
      onClose={() => setImportOpen(false)}
      departments={departments}
      branches={branches}
      workers={all}
      onImported={async (res) => {
        // Rilexo listën: importi krijoi rreshta që ky ekran s'i ka parë kurrë.
        try {
          const w = await apiService().get('workers');
          if (w?.success) setAll((w.data || []).map(normalizeWorkerRow));
        } catch (_) {}
        setToast({
          open: true,
          variant: res?.failed ? 'warning' : 'success',
          message: t('worker.import.toast', {
            defaultValue: 'U importuan {{n}} punëtorë.',
            n: res?.created || 0,
          }),
        });
      }}
    />
    </>
  );
}
